import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  noIndex?: boolean;
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  noIndex = false,
}) => {
  const location = useLocation();
  const siteName = 'Nexis ERP';
  const baseUrl = 'https://nexiserp.web.app';

  useEffect(() => {
    // Update Title
    const fullTitle = title ? `${title} | ${siteName}` : siteName;
    document.title = fullTitle;

    // Update Meta Description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description || 'Nexis ERP - High-Performance Enterprise Resource Planning');
    }

    // Update Keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (keywords && keywords.length > 0) {
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', keywords.join(', '));
    }

    // Update Canonical
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', canonical || `${baseUrl}${location.pathname}`);

    // Update Robots
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', noIndex ? 'noindex, nofollow' : 'index, follow');
    }

    // Update OpenGraph
    const ogTags = [
      { property: 'og:title', content: ogTitle || fullTitle },
      { property: 'og:description', content: ogDescription || description },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: `${baseUrl}${location.pathname}` },
      { property: 'og:image', content: ogImage || `${baseUrl}/assets/erp-logo.svg` },
      { property: 'og:site_name', content: siteName },
    ];

    ogTags.forEach(({ property, content }) => {
      if (!content) return;
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, noIndex, location]);

  return null;
};
